import {
	useInfiniteQuery,
	useMutation,
	useQuery,
	useQueryClient,
	type InfiniteData,
	type QueryClient,
} from '@tanstack/react-query';
import { queryKeys } from '@/lib/queryKeys';
import { normalizeApiError } from '@/services/httpError';
import { notificationApi } from '../api/notificationApi';
import type { AppNotification, NotificationPreference } from '../types';

export const NOTIFICATION_PAGE_SIZE = 20;
/** Fallback refresh of the badge when the realtime socket is down. */
const UNREAD_REFETCH_MS = 60_000;

export type NotificationFilter = 'all' | 'unread';

export function useNotificationList(
	filter: NotificationFilter,
	enabled = true,
) {
	return useInfiniteQuery({
		queryKey: queryKeys.notifications.list(filter),
		queryFn: ({ pageParam, signal }) =>
			notificationApi.list(
				{
					unread: filter === 'unread',
					limit: NOTIFICATION_PAGE_SIZE,
					cursor: pageParam,
				},
				signal,
			),
		initialPageParam: undefined as string | undefined,
		getNextPageParam: (lastPage) =>
			lastPage.length === NOTIFICATION_PAGE_SIZE
				? lastPage[lastPage.length - 1]?.createdAt
				: undefined,
		enabled,
	});
}

export function useUnreadCount() {
	return useQuery({
		queryKey: queryKeys.notifications.unreadCount(),
		queryFn: ({ signal }) => notificationApi.unreadCount(signal),
		select: (result) => result.count,
		refetchInterval: UNREAD_REFETCH_MS,
		refetchOnWindowFocus: true,
		// A 404 means notifications are disabled on this backend.
		retry: (failureCount, error) =>
			normalizeApiError(error).status !== 404 && failureCount < 2,
	});
}

/** True when the backend has the notification feature turned off. */
export function isNotificationsDisabled(error: unknown): boolean {
	return error != null && normalizeApiError(error).status === 404;
}

/** Mark one notification read in every cached list and the badge. */
export function applyRead(
	queryClient: QueryClient,
	id: string,
	readAt: string,
): void {
	let wasUnread = false;
	queryClient.setQueriesData<InfiniteData<AppNotification[]>>(
		{ queryKey: [...queryKeys.notifications.all, 'list'] },
		(data) =>
			data && {
				...data,
				pages: data.pages.map((page) =>
					page.map((item) => {
						if (item.id !== id || item.readAt) return item;
						wasUnread = true;
						return { ...item, readAt };
					}),
				),
			},
	);
	if (wasUnread) {
		queryClient.setQueryData<{ count: number }>(
			queryKeys.notifications.unreadCount(),
			(current) => current && { count: Math.max(0, current.count - 1) },
		);
	}
}

export function useMarkNotificationRead() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => notificationApi.markRead(id),
		onMutate: (id) => applyRead(queryClient, id, new Date().toISOString()),
		onSettled: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.notifications.unreadCount(),
			}),
	});
}

export function useMarkAllNotificationsRead() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: () => notificationApi.markAllRead(),
		onSettled: () =>
			queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all }),
	});
}

export function useNotificationPreferences(enabled = true) {
	return useQuery({
		queryKey: queryKeys.notifications.preferences(),
		queryFn: ({ signal }) => notificationApi.getPreferences(signal),
		enabled,
	});
}

export function useUpdateNotificationPreference() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (preference: NotificationPreference) =>
			notificationApi.updatePreferences([preference]),
		onSuccess: (preferences) =>
			queryClient.setQueryData(
				queryKeys.notifications.preferences(),
				preferences,
			),
	});
}

/** A channel with no stored preference is enabled (backend default). */
export function isChannelEnabled(
	preferences: NotificationPreference[] | undefined,
	category: string,
	channel: NotificationPreference['channel'],
): boolean {
	return (
		preferences?.find(
			(item) => item.category === category && item.channel === channel,
		)?.enabled ?? true
	);
}
